import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Button,
  FormControl,
  IconButton,
  LinearProgress,
  Stack,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Control, FormProvider, useForm } from 'react-hook-form';

import { createTags, removeTags } from '../../axiosClient/dictionaryApi';
import { IOptions } from '../../interfaces/IOptions';
import CheckboxButtonGroup from '../CheckboxButtonGroup';
import ControlledTextField from '../ControlledTextField';
import DrawerComponent from '../DrawerComponent';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  tagsList: IOptions[];
  isLoading: boolean;
  name: string;
  control?: Control;
}

interface IFormInput {
  name: string;
}

const TagsDrawer: React.FC<Props> = ({
  open,
  onClose,
  tagsList,
  isLoading,
  control,
  name,
}) => {
  const methods = useForm<IFormInput>();
  const { handleSubmit, reset } = methods;
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: removeTags,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ['admin_tags'],
      });
    },
  });

  const { mutate: addItem } = useMutation({
    mutationFn: createTags,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ['admin_tags'],
      });
    },
  });

  const addTags = React.useCallback(
    (payload: IFormInput) => {
      if (payload.name) {
        addItem(payload, {
          onSuccess: () => {
            reset();
          },
        });
      }
    },
    [addItem]
  );

  return (
    <DrawerComponent open={open} onClose={onClose} drawerHeader="Modify tags">
      <Stack spacing={2} sx={{ paddingX: 3 }}>
        <FormControl
          sx={{ width: '95%' }}
          component="fieldset"
          variant="standard"
        >
          {isLoading ? (
            <LinearProgress />
          ) : (
            <CheckboxButtonGroup
              name={name}
              control={control}
              options={tagsList || []}
              renderAction={(id: string) => (
                <IconButton aria-label="delete" onClick={() => mutate(id)}>
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            />
          )}
        </FormControl>
        <FormProvider {...methods}>
          <Stack spacing={2} component="form" onClick={handleSubmit(addTags)}>
            <ControlledTextField
              label="Add another item"
              name="name"
              fullWidth
            />
            <Button
              variant="contained"
              type="submit"
              sx={{
                width: '100%',
                marginTop: '10px',
              }}
            >
              + Add tag
            </Button>
          </Stack>
        </FormProvider>
      </Stack>
    </DrawerComponent>
  );
};

export default TagsDrawer;
